/* Money + plan formatting. Previously getCurrencySymbol was copy-pasted into
   Marketplace, PlanDetails and OrderDetails, and BundleDetails hardcoded "₹"
   regardless of the bundle's currency. One definition now. */

export const getCurrencySymbol = (currency) => {
  switch (currency?.toLowerCase()) {
    case 'usd':
      return '$';
    case 'inr':
      return '₹';
    default:
      return '₹'; // Default to INR if the portal omits a currency
  }
};

/** "₹499" / "$39.20" — amounts come back as plain numbers or numeric strings.
 *  Fractional amounts always get two places; whole ones get none. */
export const formatMoney = (amount, currency) => {
  const symbol = getCurrencySymbol(currency);
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${symbol}${amount ?? '—'}`;
  const fractionDigits = Number.isInteger(n) ? 0 : 2;
  return `${symbol}${n.toLocaleString('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

/**
 * Discount percentage, or null when there is no genuine saving.
 * Guards against mrp === 0, which made the old inline maths render "Infinity% OFF".
 */
export const getDiscountPercent = (mrp, cost) => {
  const m = Number(mrp);
  const c = Number(cost);
  if (!Number.isFinite(m) || !Number.isFinite(c)) return null;
  if (m <= 0 || c < 0 || m <= c) return null;
  return Math.round(((m - c) / m) * 100);
};

/** The price a bundle or plan actually charges. */
export const getItemCost = (item) =>
  item?.is_bundle ? item?.bundle_cost : item?.plan_cost;

/** Display title for a card: brand for plans, product name for bundles. */
export const getItemTitle = (item) =>
  item?.is_bundle ? item?.product_name : item?.vendor_name;

export const getItemLogo = (item) =>
  item?.is_bundle ? item?.product_logo_url : item?.vendor_logo_url;

/**
 * The id the detail routes expect.
 *
 * Bundles are serialised with `bundle_plan_id` and carry no `id` / `product_id`
 * / `plan_id` at all, so the old `item.id || item.product_id || item.plan_id`
 * evaluated to undefined: every bundle link went to /bundle-details/undefined
 * and the page could only ever render from the localStorage cache.
 */
export const getItemId = (item) =>
  item?.is_bundle
    ? item?.bundle_plan_id || item?.id || item?.product_id
    : item?.plan_id || item?.id;

/** Error text from the partner API, which replies with `error` on failure and
 *  only uses `message` on success. */
export const apiErrorMessage = (result, fallback) => {
  const raw = result?.error ?? result?.message;
  if (!raw) return fallback;
  if (typeof raw === 'string') return raw;
  // Schema validation errors come back as a dict of field -> [messages].
  try {
    return Object.values(raw).flat().join(' ') || fallback;
  } catch {
    return fallback;
  }
};

/**
 * Secondary line under a brand name.
 *
 * Several partner plans are stored with the plan name copied into the vendor
 * name, which rendered the same string twice on every card. Only return the
 * plan name when it actually adds something.
 */
export const getPlanSubtitle = (plan) => {
  const brand = (plan?.vendor_name || '').trim();
  const name = (plan?.plan_name || '').trim();
  if (!name) return null;
  const a = brand.toLowerCase().replace(/\s+/g, ' ');
  const b = name.toLowerCase().replace(/\s+/g, ' ');
  if (!a) return name;
  if (a === b || a.includes(b)) return null;
  return name;
};
